import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Gift, Send, CheckCircle, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PublicReferralPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    referrerName: '',
    referrerEmail: '',
    referredName: '',
    referredEmail: '',
    referredPhone: '',
    referredAddress: '',
    referredNotes: ''
  });

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews/public/campaign/${campaignId}`);
        setCampaign(res.data);
        
        // Pre-fill referrer info if available
        if (res.data.customer_name) {
          setFormData(prev => ({ ...prev, referrerName: res.data.customer_name }));
        }
      } catch (err) {
        setError('Referral page not found or has expired.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/reviews/public/submit-referral/${campaignId}`, {
        referrer_name: formData.referrerName,
        referrer_email: formData.referrerEmail,
        referred_name: formData.referredName,
        referred_email: formData.referredEmail || null,
        referred_phone: formData.referredPhone || null,
        referred_address: formData.referredAddress || null,
        referred_notes: formData.referredNotes || null
      });
      
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit referral. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-slate-300 text-lg mb-6">
            Your referral has been submitted successfully. We'll reach out to {formData.referredName} soon!
          </p>
          <p className="text-slate-400">
            {campaign.contractor_company || campaign.contractor_name} appreciates your trust and support.
          </p>
          
          {/* Option to add another referral */}
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData(prev => ({
                ...prev,
                referredName: '',
                referredEmail: '',
                referredPhone: '',
                referredAddress: '',
                referredNotes: ''
              }));
            }}
            className="mt-8 bg-emerald-600 hover:bg-emerald-700"
          >
            <Gift className="w-4 h-4 mr-2" />
            Refer Another Friend
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">
            Refer a Friend
          </h1>
          <p className="text-slate-300 text-lg">
            Know someone who needs roofing help?{' '}
            <span className="text-emerald-400 font-semibold">
              {campaign?.contractor_company || campaign?.contractor_name}
            </span>{' '}
            would love to help them!
          </p>
        </div>

        {/* Referral Form */}
        <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-slate-700">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Your Info Section */}
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-400" />
              Your Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Your Name *</label>
                <Input
                  name="referrerName"
                  value={formData.referrerName}
                  onChange={handleChange}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Your Email *</label>
                <Input
                  name="referrerEmail"
                  type="email"
                  value={formData.referrerEmail}
                  onChange={handleChange}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {/* Friend Info Section */}
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-400" />
              Friend's Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Friend's Name *</label>
                <Input
                  name="referredName"
                  value={formData.referredName}
                  onChange={handleChange}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Jane Doe"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Email</label>
                  <Input
                    name="referredEmail"
                    type="email"
                    value={formData.referredEmail}
                    onChange={handleChange}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="jane@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Phone</label>
                  <Input
                    name="referredPhone"
                    type="tel"
                    value={formData.referredPhone}
                    onChange={handleChange}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Property Address</label>
                <Input
                  name="referredAddress"
                  value={formData.referredAddress}
                  onChange={handleChange}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="123 Main St, City, State"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Additional Notes</label>
                <Textarea
                  name="referredNotes"
                  value={formData.referredNotes}
                  onChange={handleChange}
                  className="bg-slate-700 border-slate-600 text-white min-h-[80px]"
                  placeholder="Any details about their roofing needs..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Referral
              </>
            )}
          </Button>

          <p className="text-center text-slate-500 text-sm mt-4">
            We'll reach out to your friend with a friendly introduction. Your information will only be used to mention who referred them.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicReferralPage;
